import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from "@headlessui/react";
import { cn } from "~/lib/utils";
import { useRouter } from "next/navigation";
import { RecentSearchItem, SearchResultItem } from "../../types";
import React, { MouseEvent, useRef, useState } from "react";
import { useNavigatorPlatform } from "~/modules/app/hooks/useNavigatorPlatform";
import { HotkeyBadge } from "./HotkeyBadge";
import { EmptyState } from "./EmptyState";
import { LoadingState } from "./LoadingState";
import { SearchResultIcon } from "./SearchIcon";
import { toRecentSearchResultItem } from "../../util/recentSearches";

// // // //

function SearchWindowOption(props: {
    item: SearchResultItem;
    altPrefix?: string;
}) {
    const { item, altPrefix = item.content.label } = props;

    return (
        <ComboboxOption
            as="li"
            value={item}
            className={({ focus }) =>
                cn(
                    "cursor-pointer select-none px-4 py-2 flex items-center gap-3",
                    {
                        // NOTE - focus state here matches SidebarNav
                        "bg-muted text-link": focus,
                    },
                )
            }
        >
            <SearchResultIcon
                src={item.content.icon}
                className={item.content.iconClassName}
                wrapperClassName={item.content.iconWrapperClassName}
                alt={`${altPrefix} Search Result Icon`}
            />

            <p className="text-base">{item.content.label}</p>

            {item.content.subtitle && (
                <p className="text-muted-foreground text-sm">
                    {item.content.subtitle}
                </p>
            )}
        </ComboboxOption>
    );
}

/**
 * Renders the search input and results window for GlobalSearch and CommandPalette components
 */
export function SearchWindow(props: {
    setOpen: (bool: boolean) => void;
    autofocus?: boolean;
    groups: Record<string, SearchResultItem[]>;
    query: string;
    onOpen?: () => void;
    onClose: () => void;
    setQuery: (q: string) => void;
    items: SearchResultItem[];
    loadingSearch?: boolean;
    hideResults?: boolean;
    showInteractionTip?: boolean;
    onSelectResult: (result: SearchResultItem) => void;
    recentSearches?: RecentSearchItem[];
    onClearRecentSearches?: () => void;
    size?: "md" | "lg";
}) {
    const {
        query,
        setOpen,
        onOpen,
        onClose,
        setQuery,
        autofocus,
        items,
        groups,
        loadingSearch = false,
        hideResults = false,
        showInteractionTip = false,
        recentSearches = [],
        onClearRecentSearches,
        size = "lg",
        onSelectResult,
    } = props;

    const router = useRouter();
    const [focused, setFocused] = useState(false);
    const { isAppleOS } = useNavigatorPlatform();
    const inputRef = useRef<HTMLInputElement>(null);
    const recentSearchResultItems = recentSearches.map(
        toRecentSearchResultItem,
    );

    const canShowResults = !hideResults || focused;
    const showRecentSearches =
        canShowResults && query === "" && recentSearchResultItems.length > 0;
    const showGroupedResults = canShowResults && items.length > 0;

    // Define flag to show the search results content when appropriate
    const showWindowContent = showRecentSearches || showGroupedResults;

    // Define flag to show usage info when appropriate
    // const showUsageInfo = showWindowContent && query === "";

    // Define flag to show loading state when appropriate
    const showLoadingState = (!hideResults || focused) && loadingSearch;

    // Define flag to show empty state when appropriate
    const showEmptyState =
        query !== "" && canShowResults && items.length === 0 && !loadingSearch;

    const handleClearRecentSearches = (
        event: MouseEvent<HTMLButtonElement>,
    ) => {
        event.preventDefault();
        event.stopPropagation();
        onClearRecentSearches?.();
    };

    return (
        <Combobox
            onChange={(item: SearchResultItem) => {
                // ENHANCEMENT - add support for "open in new tab"
                // if (openInNewTab) {
                //     window.open(item.href, "_blank");
                //     return;
                // }
                if (!item) {
                    return;
                }

                router.push(item.href);
                onSelectResult(item);
                setOpen(false);

                // Blur the Combobox.Input element
                inputRef?.current?.blur();
            }}
        >
            <div className="relative">
                <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    className={cn(
                        "pointer-events-none absolute left-4 text-muted-foreground",
                        {
                            "top-3 h-4 w-4": size === "md",
                            "top-3.5 h-5 w-5": size === "lg",
                        },
                    )}
                    aria-hidden="true"
                />
                <ComboboxInput
                    ref={inputRef}
                    autoFocus={autofocus}
                    placeholder="Search any asset, issuer, ticker, or entity..."
                    className={cn(
                        "w-full border-border border-0 bg-transparent pl-11 pr-4 text-base md:text-sm font-normal text-slate-900 placeholder:font-light placeholder:text-muted-foreground focus:ring-0",
                        {
                            "border-b!": focused,
                            "h-10": size === "md",
                            "h-12": size === "lg",
                        },
                    )}
                    onFocus={() => {
                        setFocused(true);
                        if (onOpen) {
                            onOpen();
                        }
                    }}
                    onBlur={() => {
                        // NOTE - we wrap this in a setTimeout
                        // because hiding the Combobox.Options below without a delay will remove thier UI
                        // before the user's click event can register on the element.
                        setTimeout(() => {
                            setFocused(false);
                            setOpen(false);
                            setQuery("");
                            onClose();
                        }, 100);
                    }}
                    onChange={(event) => setQuery(event.target.value)}
                />

                {/* Conditionally render HotkeyBadge */}
                {showInteractionTip && <HotkeyBadge isAppleOS={isAppleOS} />}
            </div>

            {/* Show usage info */}
            {/* Figure out how this should work and the interaction logic behind it */}
            {/* {showUsageInfo && <UsageInfo />} */}

            {/* Conditionally show loading state */}
            {showLoadingState && <LoadingState />}

            {/* Conditionally show empty state */}
            {showEmptyState && <EmptyState />}

            {showWindowContent && !showLoadingState && (
                <ComboboxOptions
                    as="ul"
                    static
                    className="max-h-96 scroll-pb-2 scroll-pt-11 space-y-2 overflow-y-auto pb-2"
                >
                    {showRecentSearches && (
                        <li key="recent-searches">
                            <div className="bg-card px-4 py-2.5 text-sm font-normal text-muted-foreground border-b flex items-center justify-between gap-4">
                                <h2>Recent Searches</h2>
                                {onClearRecentSearches && (
                                    <button
                                        type="button"
                                        className="text-xs font-medium text-link hover:text-link/80"
                                        onMouseDown={(event) =>
                                            event.preventDefault()
                                        }
                                        onClick={handleClearRecentSearches}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            <ul className="mt-2 text-foreground">
                                {recentSearchResultItems.map((item) => (
                                    <SearchWindowOption
                                        key={item.id}
                                        item={item}
                                        altPrefix={`${item.content.label} Recent`}
                                    />
                                ))}
                            </ul>
                        </li>
                    )}

                    {Object.entries(groups).map(([category, nested], index) => (
                        <li key={category}>
                            <h2
                                className={cn(
                                    "bg-card px-4 py-2.5 text-sm font-normal text-muted-foreground border-b",
                                    {
                                        "border-t":
                                            showRecentSearches || index > 0,
                                    },
                                )}
                            >
                                {category}
                            </h2>
                            <ul className="mt-2 text-foreground">
                                {/* Only render the first 20 items to avoid performance issues */}
                                {nested.slice(0, 20).map((item) => (
                                    <SearchWindowOption
                                        key={item.id}
                                        item={item}
                                    />
                                ))}
                            </ul>
                        </li>
                    ))}
                </ComboboxOptions>
            )}
        </Combobox>
    );
}
